import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { BrandLogo } from './AppChrome';

/*
 * CURATED, NOT EXHAUSTIVE. Every slug here resolves to a real vector mark that
 * is a GLYPH — no wordmarks, and nothing that only exists as a favicon. A
 * favicon bakes in its own opaque plate, which shows up as a coloured square
 * sitting inside our tile, and a wordmark reads as illegible noise at 28px.
 * Anything that cannot clear both bars is left out rather than patched: the
 * point of the band is instant recognition, so an unrecognisable tile costs
 * more than a missing one.
 */
/*
 * ONE BAND, not two. A second row doubled the height of the block without
 * adding any information, and its shorter length left a ragged end mid-frame.
 * A single line reads as a strip of a much longer list, which is the point.
 * Order is mixed rather than grouped so no stretch of the band looks like a
 * category.
 */
export const TOOLS = ['slack', 'notion', 'salesforce', 'microsoftteams', 'linear', 'googlesheets', 'jira', 'intercom', 'hubspot', 'shopify', 'github', 'zendesk', 'figma', 'dropbox', 'stripe', 'airtable', 'googledrive', 'trello', 'gmail', 'gitlab', 'zoom', 'zapier', 'asana', 'snowflake', 'confluence', 'quickbooks', 'mailchimp', 'clickup'];
export function Tile({
  slug
}: {
  slug: string;
}) {
  return <span className="flex size-[54px] shrink-0 items-center justify-center rounded-[14px] border border-white/[0.07] bg-white/[0.035] md:size-16 md:rounded-2xl" data-id="element-1099" data-mp="20d4irtzqdfff">
      {/* Flattened to one value. Twenty-eight brands' worth of unrelated
       corporate colour was the loudest thing in the band; as white
       silhouettes the row reads as our directory rather than a collage of
       other companies' logos. */}
      <BrandLogo slug={slug} onDark mono className="size-6 opacity-70 md:size-7" data-id="element-1100" data-mp="1hog25k8a5w9g" />
    </span>;
}

/**
 * One band. The track holds the row twice over, so translating it by exactly
 * half its width returns to a pixel-identical frame — that is what makes the
 * loop seamless rather than snapping back at the end.
 *
 * Continuous motion, so the easing is linear and the duration is long: this is
 * ambient evidence sitting under a claim, not something the reader is meant to
 * watch happen.
 */
function Band({
  slugs,
  reverse = false,
  duration,
  intro = false
}: {
  slugs: string[];
  reverse?: boolean;
  duration: number;
  /** Pop the tiles in from the middle of the frame outward before rolling. */
  intro?: boolean;
}) {
  const track = [...slugs, ...slugs];
  /*
   * THE ARRIVAL HAPPENS ON THIS TRACK, not on a stand-in above it.
   *
   * It used to be a separate centred row of 15 tiles crossfading into this
   * band — a different set of logos at different x positions, so at the
   * handover every mark jumped. Popping the real track's own tiles in and
   * then starting its travel means there is nothing to hand over to: the
   * marks you watch arrive are the marks that then move.
   *
   * Roughly 15 tiles are visible at a time, so the middle of the frame is
   * near index 7 — the stagger radiates from there and clamps, so tiles
   * off-frame do not wait on a delay nobody sees.
   */
  const CENTRE = 7;
  const stagger = (i: number) => Math.min(Math.abs(i - CENTRE), 9) * 0.035;
  return <motion.div className="flex w-max gap-3 md:gap-3.5" animate={{
    x: reverse ? ['-50%', '0%'] : ['0%', '-50%']
  }} transition={{
    duration,
    repeat: Infinity,
    ease: 'linear',
    // Held until the set has landed: a row that starts moving while it is
    // still appearing reads as two things happening at once.
    delay: intro ? 0.62 : 0
  }} data-id="element-1101" data-mp="2ylf0ck77u5of">
      {track.map((slug, i) => intro ? <motion.span key={`${slug}-${i}`} className="flex" initial={{
      opacity: 0,
      scale: 0.7
    }} animate={{
      opacity: 1,
      scale: 1
    }} transition={{
      duration: 0.32,
      delay: stagger(i),
      ease: [0.16, 1, 0.3, 1]
    }} data-id="element-1102" data-mp="3idlcfi35v78v">
          <Tile slug={slug} data-id="element-1103" data-mp="2ayglfqyqv5sm" />
        </motion.span> : <Tile key={`${slug}-${i}`} slug={slug} data-id="element-1104" data-mp="2r4tjcgdha8sz" />)}
    </motion.div>;
}

/**
 * The directory, shown rather than claimed.
 *
 * The band is masked to nothing at the left and right edges rather than
 * clipped in a panel. A hard edge would state that the set ends exactly where
 * the container does — the fade says it carries on past the frame, which is the
 * whole point of a 4,000-tool directory.
 */
export function ToolMarquee({
  intro = false
}: {
  /** Play the tiles in once, then start the loop. */
  intro?: boolean;
} = {}) {
  const reduce = useReducedMotion();
  const mask = 'linear-gradient(to right, transparent, #000 14%, #000 86%, transparent)';

  // Under reduced motion the band holds still. A static, centred row makes the
  // same point without anything moving.
  if (reduce) {
    return <div aria-hidden="true" className="flex flex-wrap justify-center gap-3" data-id="element-1105" data-mp="1oqcr5x3j7shi">
        {TOOLS.slice(0, 12).map(slug => <Tile key={slug} slug={slug} data-id="element-1106" data-mp="1ko845h7l9u3u" />)}
      </div>;
  }
  return <div aria-hidden="true" className="relative overflow-hidden" style={{
    maskImage: mask,
    WebkitMaskImage: mask
  }} data-id="element-1107" data-mp="1kqu9rbax5zjf">
      <Band slugs={TOOLS} duration={92} intro={intro} data-id="element-1108" data-mp="480xmgki1ep0" />
    </div>;
}